/**
 * Authentication hook
 * Provides user info and role-based authorization helpers
 */

import { useState, useEffect } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

export interface User {
  email: string;
  sub: string;
  givenName: string;
  familyName: string;
  groups: string[];
  role: 'admin' | 'instructor' | 'student';
}

/**
 * Role hierarchy (matches backend)
 */
const ROLE_PRECEDENCE = {
  admin: 3,
  instructor: 2,
  student: 1,
} as const;

/**
 * Determine user role from Cognito groups
 * Role precedence: admin > instructor > student
 */
function determineRole(groups: string[]): 'admin' | 'instructor' | 'student' {
  if (groups.includes('admins')) {
    return 'admin';
  }
  if (groups.includes('instructors')) {
    return 'instructor';
  }
  return 'student';
}

/**
 * Check if user has minimum required role
 * Examples:
 *   hasMinimumRole('admin', 'instructor') => true (admin >= instructor)
 *   hasMinimumRole('student', 'instructor') => false (student < instructor)
 */
export function hasMinimumRole(
  userRole: 'admin' | 'instructor' | 'student',
  requiredRole: 'admin' | 'instructor' | 'student'
): boolean {
  return ROLE_PRECEDENCE[userRole] >= ROLE_PRECEDENCE[requiredRole];
}

/**
 * Authentication hook
 * Fetches current user from Amplify and determines role from Cognito groups
 * In dev mode, returns a mock admin user
 */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';

  useEffect(() => {
    loadUser();
  }, []);

  async function loadUser() {
    try {
      setLoading(true);
      setError(null);

      // Dev mode: Use mock admin user
      if (isDevMode) {
        setUser({
          email: 'dev@example.com',
          sub: 'dev-user-123',
          givenName: 'Dev',
          familyName: 'Admin',
          groups: ['admins', 'instructors', 'students'],
          role: 'admin',
        });
        setLoading(false);
        return;
      }

      // Production mode: Get from Cognito
      // Get current authenticated user (validates auth state)
      await getCurrentUser();

      // Get session with tokens
      const session = await fetchAuthSession();

      if (!session.tokens?.idToken) {
        throw new Error('No ID token found');
      }

      const idToken = session.tokens.idToken.payload;

      // Extract user info from JWT claims
      const email = idToken.email as string;
      const sub = idToken.sub as string;
      const givenName = (idToken.given_name as string) || '';
      const familyName = (idToken.family_name as string) || '';
      const groups = (idToken['cognito:groups'] as string[]) || [];

      // Determine role from groups
      const role = determineRole(groups);

      setUser({
        email: email.toLowerCase(),
        sub,
        givenName,
        familyName,
        groups,
        role,
      });
    } catch (err) {
      console.error('Failed to load user:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Check if current user has minimum required role
   */
  function hasRole(requiredRole: 'admin' | 'instructor' | 'student'): boolean {
    if (!user) return false;
    return hasMinimumRole(user.role, requiredRole);
  }

  /**
   * Get display name for user
   */
  function getDisplayName(): string {
    if (!user) return '';
    return `${user.givenName} ${user.familyName}`.trim() || user.email;
  }

  /**
   * Get role display name
   */
  function getRoleDisplayName(): string {
    if (!user) return '';
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  }

  return {
    user,
    loading,
    error,
    hasRole,
    getDisplayName,
    getRoleDisplayName,
    refreshUser: loadUser,
  };
}
